<template>
  <section class="px-4 sm:px-6 lg:px-8 flow-root h-[calc(100vh-360px)] overflow-auto">
    <div v-if="props.logs.length > 0" class="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
      <div class="inline-block min-w-full py-2 align-middle">
        <table class="min-w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th scope="col" class="sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-2.5 pr-3 pl-4 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter sm:pl-6 lg:pl-8">Start Date</th>
              <th scope="col" class="sticky top-0 z-10 hidden border-b border-gray-300 bg-white/75 px-3 py-2.5 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter sm:table-cell">Provider</th>
              <th scope="col" class="sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-2.5 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter">Type</th>
              <th scope="col" class="sticky top-0 z-10 hidden border-b border-gray-300 bg-white/75 px-3 py-2.5 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter lg:table-cell">Widget Name</th>
              <th scope="col" class="sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-2.5 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter">Reference ID</th>
              <th scope="col" class="sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-2.5 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter">Status</th>
              <th scope="col" class="sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-2.5 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter">Amount</th>
              <th scope="col" class="sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-3 py-2.5 text-left text-sm font-semibold text-dark backdrop-blur-sm backdrop-filter">Response Payload</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(log, logIdx) in props.logs"
              :key="`${log.id}-${logIdx}`"
              class="cursor-pointer transition-colors hover:bg-gray-50"
              @click="emit('detail:open', log)"
            >
              <td :class="[cellBorder(logIdx), 'py-2.5 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-secondary sm:pl-6 lg:pl-8']">{{ log.transactionTimestamp }}</td>
              <td :class="[cellBorder(logIdx), 'hidden px-3 py-2.5 text-sm whitespace-nowrap text-secondary sm:table-cell']">{{ log.provider }}</td>
              <td :class="[cellBorder(logIdx), 'px-3 py-2.5 text-sm whitespace-nowrap']">
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  :class="typeBadge(log.type).classes"
                >
                  {{ typeBadge(log.type).label }}
                </span>
                <p v-if="log.source" class="mt-1 text-xs text-gray-400">via {{ log.source }}</p>
              </td>
              <td :class="[cellBorder(logIdx), 'hidden px-3 py-2.5 text-sm whitespace-nowrap text-secondary lg:table-cell']">{{ log.widgetName }}</td>
              <td :class="[cellBorder(logIdx), 'px-3 py-2.5 text-sm whitespace-nowrap text-secondary']">
                <span v-if="log.transactionReferenceId" class="inline-flex items-center gap-1.5">
                  {{ log.transactionReferenceId }}
                  <button
                    type="button"
                    class="text-gray-400 hover:text-primary-main transition-colors"
                    :title="copiedId === log.id ? 'Copied!' : 'Copy reference ID'"
                    @click.stop="copyReference(log)"
                  >
                    <CopyCheckIcon :copied="copiedId === log.id" />
                  </button>
                </span>
                <span v-else>-</span>
              </td>
              <td :class="[cellBorder(logIdx), 'px-3 py-2.5 text-sm whitespace-nowrap']">
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  :class="statusBadge(log.statusCode).classes"
                  :title="log.transactionStatus || ''"
                >
                  {{ statusBadge(log.statusCode).label }}
                </span>
              </td>
              <td :class="[cellBorder(logIdx), 'px-3 py-2.5 text-sm whitespace-nowrap text-secondary']">{{ log.amount || '-' }}</td>
              <td :class="[cellBorder(logIdx), 'relative py-2.5 pr-4 pl-3 text-sm font-medium whitespace-nowrap sm:pr-8 lg:pr-8']">
                <button
                  v-if="log.responsePayload"
                  type="button"
                  class="text-action-primary hover:text-primary-dark cursor-pointer transition-all duration-300"
                  @click.stop="emit('payload-modal:open', log.responsePayload)"
                >
                  View Payload
                </button>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-else class="flex justify-center items-center h-full">
      <div class="flex justify-center items-center flex-shrink-0 w-8 h-8 rounded-sm warning">
        <WarningIcon />
      </div>
      <p class="pl-2 text-secondary">No logs found</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import WarningIcon from "../icons/WarningIcon.vue";
import CopyCheckIcon from './CopyCheckIcon.vue';

import { PaymentGatewayLogsI } from '@/interfaces/interfaces';
import { statusBadge, typeBadge } from './logBadges';
import { copyText } from './clipboard';

const props = defineProps<{
  logs: PaymentGatewayLogsI[];
}>();

const emit = defineEmits<{
  (e: 'payload-modal:open', payload: string | JSON): void;
  (e: 'detail:open', log: PaymentGatewayLogsI): void;
}>();

const copiedId = ref<number | null>(null);

const cellBorder = (logIdx: number) => logIdx !== props.logs.length - 1 ? 'border-b border-gray-200' : '';

const copyReference = (log: PaymentGatewayLogsI) => {
  if (!log.transactionReferenceId) {
    return;
  }
  copyText(log.transactionReferenceId).then(() => {
    copiedId.value = log.id;
    setTimeout(() => {
      if (copiedId.value === log.id) {
        copiedId.value = null;
      }
    }, 1500);
  });
};
</script>
